import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { clearStorage } from "../utils/localStorage";

/**
 * Idle-logout hook.
 *
 * Logs the user out after `idleMs` of no input activity (clicks, keys, mouse
 * moves, scroll, touch). Shows a 2-minute "are you still there?" warning
 * before the actual logout so the user can stay logged in with one click.
 *
 * Synced across tabs via localStorage — typing in tab B counts as activity
 * in tab A, so a user with the CRM open in multiple tabs doesn't get logged
 * out of the inactive ones while they're working in another.
 *
 * Defaults match industry CRM norms (Salesforce: 2h, HubSpot: 24h, Pipedrive:
 * 7d). 60 min is a deliberate middle ground — long enough that no one gets
 * booted mid-task, short enough that an unattended laptop in a coffee shop
 * doesn't sit logged in for hours.
 *
 * Skipped automatically on public/auth routes so the warning modal never
 * pops up on /login, /pricing, /property/:id, /sign/:doc/:token, etc.
 */
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/pricing",
  "/pricing-summary",
  "/payment",
  "/privacy-policy",
  "/terms-and-conditions",
  "/cookies-policy",
  "/refund-cancellation-policy",
  "/sms-opt-in",
  "/sms-opt-out",
  "/unsubscribe",
  "/not-interested",
  "/interest-confirmed",
  "/real-estate",
  "/wholesalers",
  "/probate",
  "/probate-specialists",
  "/home-flippers",
  "/property/",
  "/site/",
  "/sign/",
  "/buyer-portal/",
  "/seller-portal/",
  "/client-portal/",
  "/deal-package/",
  "/case-timeline/",
  "/upload-docs/",
  "/vendor-review/",
  "/open-house/",
  "/feedback/",
  "/affiliate/",
  "/affiliate-questionnaire/",
  "/affiliate-agreement/",
  "/team-login/",
  "/ref/",
  "/r/",
];

const ACTIVITY_KEY = "__last_user_activity_ms__";
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
  "mousemove",
];

// mousemove fires constantly — debounce activity updates so we're not
// thrashing localStorage with a write every frame.
const ACTIVITY_THROTTLE_MS = 5000;

export default function useIdleLogout({
  idleMs = 60 * 60 * 1000, // 60 minutes
  warningMs = 2 * 60 * 1000, // show "stay logged in?" 2 min before logout
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000));
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tickerRef = useRef(null);
  const lastActivityWrittenRef = useRef(0);

  // Public routes shouldn't have an idle gate — don't even mount the warning.
  const isPublicRoute = PUBLIC_PATH_PREFIXES.some((p) =>
    location.pathname === p || location.pathname.startsWith(p)
  );

  const performLogout = useCallback(async () => {
    try {
      const token = localStorage.getItem("__login_user_token__");
      const refreshToken = localStorage.getItem("__refresh_token__");
      const apiUrl =
        import.meta.env.VITE_API_URL ||
        "https://stacked-backend-gv43.onrender.com/api/v1";
      if (token) {
        // Fire-and-forget — if the backend logout fails (e.g. token already
        // expired) we still want to clear local state and bounce to /login.
        fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken, reason: "idle_timeout" }),
        }).catch(() => {});
      }
    } catch {}
    // Clear ALL session state before redirecting — this is the step the
    // regular Logout button does (clearStorage) that this used to skip.
    // Without it, the __user__ blob, auth token, and (critically) the
    // stale __last_user_activity_ms__ stamp survive the reload. Login's
    // auto-redirect effect then sees the leftover __user__ blob and
    // bounces to the dashboard, which re-mounts useIdleLogout, which
    // re-reads the SAME stale stamp and fires performLogout again — an
    // infinite /login ↔ dashboard blink that locks the user out of the
    // sign-in form entirely. clearStorage preserves device-level prefs
    // (cookie consent, PWA timers) via its allowlist.
    clearStorage();
    // Full reload to drop the Redux store / RTK Query caches — same pattern
    // as the regular Logout button so the next user on this device starts
    // clean.
    window.location.href = "/login?reason=idle_timeout";
  }, []);

  const scheduleTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (tickerRef.current) clearInterval(tickerRef.current);
    setShowWarning(false);
    warningTimerRef.current = setTimeout(() => {
      setSecondsLeft(Math.floor(warningMs / 1000));
      setShowWarning(true);
      // Update countdown text every second
      tickerRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 1 ? s - 1 : 0));
      }, 1000);
    }, idleMs - warningMs);
    logoutTimerRef.current = setTimeout(() => {
      performLogout();
    }, idleMs);
  }, [idleMs, warningMs, performLogout]);

  const markActive = useCallback(() => {
    const now = Date.now();
    // Throttle the localStorage write — mousemove fires hundreds of times
    // per second. One write every 5s is plenty for cross-tab sync.
    if (now - lastActivityWrittenRef.current > ACTIVITY_THROTTLE_MS) {
      lastActivityWrittenRef.current = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {}
    }
    scheduleTimers();
  }, [scheduleTimers]);

  // Cross-tab sync: when another tab writes to ACTIVITY_KEY, treat that as
  // local activity so the user doesn't get logged out of an open-but-idle
  // tab while they're actively working in a different tab.
  useEffect(() => {
    if (isPublicRoute) return;
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY) scheduleTimers();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [isPublicRoute, scheduleTimers]);

  // Wire activity listeners + first schedule
  useEffect(() => {
    if (isPublicRoute) return;
    // If the user already had activity in another tab while this tab was
    // closed, we want to honor that — start the timer from THEIR last
    // activity, not from this mount.
    let lastActivity = 0;
    try {
      lastActivity = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
    } catch {}
    const now = Date.now();
    const sinceLast = now - lastActivity;
    // Sanity floor: ignore any ACTIVITY_KEY whose value would mean
    // "user was last active more than 24h ago." That value isn't
    // a real signal — it's leftover state from a previous browser
    // session, a different user on this device, or a long-closed
    // tab. Treating it as "already past idle threshold" would
    // immediately log this fresh session out and bounce them into
    // the /login?reason=idle_timeout loop. The cross-tab activity
    // sync this check exists for would never write a stamp >24h
    // old because the writing tab would already be logged out.
    const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
    if (lastActivity && sinceLast < STALE_THRESHOLD_MS && sinceLast >= idleMs) {
      // They were already past the threshold before this tab loaded.
      performLogout();
      return;
    }
    scheduleTimers();
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActive, { passive: true })
    );
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (tickerRef.current) clearInterval(tickerRef.current);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, markActive)
      );
    };
  }, [isPublicRoute, idleMs, markActive, scheduleTimers, performLogout]);

  const stayLoggedIn = () => markActive();
  const logoutNow = () => performLogout();

  if (isPublicRoute || !showWarning) return null;

  // Inline rendered warning modal — caller doesn't need to provide UI.
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-warn-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="max-w-md w-full bg-[#1a1a1b] border border-[var(--primary-color)]/40 rounded-2xl p-6 shadow-2xl text-center">
        <h2
          id="idle-warn-title"
          className="text-xl font-bold text-white mb-2"
        >
          Still there?
        </h2>
        <p className="text-gray-300 mb-1">
          You've been inactive for a while. For your account's security we'll
          log you out automatically in:
        </p>
        <p className="text-3xl font-bold text-[var(--primary-color)] my-4 tabular-nums">
          {Math.floor(secondsLeft / 60)}:
          {String(secondsLeft % 60).padStart(2, "0")}
        </p>
        <div className="flex gap-3 mt-4">
          <button
            type="button"
            onClick={logoutNow}
            className="flex-1 py-3 bg-gray-800 text-gray-300 rounded-xl font-medium hover:bg-gray-700"
          >
            Log out now
          </button>
          <button
            type="button"
            onClick={stayLoggedIn}
            className="flex-1 py-3 bg-[var(--primary-color)] text-[#1E2A38] rounded-xl font-semibold hover:brightness-110"
            autoFocus
          >
            Stay logged in
          </button>
        </div>
      </div>
    </div>
  );
}
